import './RemarksTab.scss';

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Avatar,
  Typography,
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import moment from 'moment';
import { useParams } from 'react-router-dom';

import { UserOutlined } from '@ant-design/icons';

import UserAPI from '../../../../api/UserAPI';
import ChatTime from '../../../../components/ChatTime';
import CustomReadMoreText
  from '../../../../components/CustomReadMoreText/CustomReadMoreText';
import { DEFAULT_AVATAR } from '../../../../shared/data';
import showAppError from '../../../../shared/error';
import { useAppSelector } from '../../../../shared/hooks';

const formatDate = (prev, next, index) => {
  const previousDay = moment(prev).startOf('day');
  const today = moment().startOf('day');
  const nextDay = moment(next).startOf('day');

  const dateDiff = nextDay.diff(previousDay, 'days');

  if (index !== 0 && dateDiff <= 0) return;

  if (today.diff(nextDay, 'days') < 1) return <ChatTime dateString="TODAY" />;

  if (today.diff(nextDay, 'days') < 2)
    return <ChatTime dateString="YESTERDAY" />;

  if (today.diff(nextDay, 'days') <= 6)
    return <ChatTime dateString={moment(next).format('dddd')} />;

  return <ChatTime dateString={moment(next).format('DD/MM/YYYY')} />;
};

const RemarksTab = () => {
  const { userId } = useParams();
  const listRef = useRef(null);

  const userDetails = useAppSelector((state) => state.user);

  const [remarksList, setRemarksList] = useState([]);
  const [remark, setRemark] = useState('');
  // const [shiftPressedDown, setShiftPressedDown] = useState(false);
  const [loader, setLoader] = useState(false);

  const fetchRemarks = async () => {
    try {
      const res = await UserAPI.getAllRemarks(userId);
      if (res.data.code === 0) {
        setRemarksList(res.data.result);
      } else showAppError(res || 'Failed to fetch remarks!');
    } catch (err) {
      showAppError(err || 'Failed to fetch remarks!');
    }
  };

  const handleCreateRemark = async () => {
    if (remark.trim() === '') {
      return false;
    }

    setLoader(true);

    try {
      const res = await UserAPI.createRemark(userId, { message: remark });
      if (res.code === 0) {
        setRemark('');
        setRemarksList((prev) => [
          ...prev,
          {
            _id: new Date(),
            updatedAt: new Date(),
            description: remark,
            remarkBy: {
              name: userDetails?.name,
              profilePicUrl: userDetails?.profilePic,
            },
          },
        ]);
      } else showAppError(res || 'Failed to create remark!');
    } catch (err) {
      showAppError(err || 'Failed to create remark!');
    } finally {
      setLoader(false);
    }
  };
  const scrollToBottom = () => {
    if (!listRef.current) return () => {};
    // Defer until after layout/paint so scrollHeight is correct
    const run = () => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
      // Also scroll the page's scroll container so user sees full view with input at bottom
      const pageScroll = listRef.current?.closest?.(
        '.user-profile__content__tabs',
      );
      if (pageScroll) {
        pageScroll.scrollTop = pageScroll.scrollHeight;
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    const t = setTimeout(run, 200);
    return () => clearTimeout(t);
  };

  useEffect(() => {
    fetchRemarks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On load and when remarks change: scroll to bottom so user sees full view with input at bottom
  useEffect(() => {
    const cancel = scrollToBottom();
    return () => (typeof cancel === 'function' ? cancel() : undefined);
  }, [remarksList]);

  return (
    <>
      <div className="remarkListWrapper user-profile__tab user-profile__tab--remarks">
        <div className="remarkList slimScroll transparent" ref={listRef}>
          <Typography.Title level={5} className="remarks-heading">
            Remarks
          </Typography.Title>
          {remarksList?.map((data, index) => {
            return (
              <div key={data?._id}>
                <div className="dateString">
                  {formatDate(
                    remarksList[index - 1]?.updatedAt,
                    data?.updatedAt,
                    index,
                  )}
                </div>
                <div className="remarkListItem" key={data._id}>
                  <span>
                    <Avatar
                      src={data?.remarkBy?.profilePicUrl || DEFAULT_AVATAR}
                      alt="RemarkBy"
                      size={32}
                    />
                    <div className="remarkContainer">
                      <Typography.Text className="remarkAuthor">
                        {data.remarkBy?.name}
                      </Typography.Text>

                      {/* <Linkify>
                        <Typography.Paragraph
                          ellipsis={{
                            rows: 3,
                            symbol: 'See more',
                            expandable: true,
                          }}
                          className="remarkText"
                          id={`${data._id}`}
                        >
                          {data.description}
                        </Typography.Paragraph>
                      </Linkify> */}

                      <CustomReadMoreText text={data.description || ''} />

                      <div className="remark-item-footer">
                        <span className="remarkTime">
                          {moment(data?.updatedAt).format('h:mm A')}
                        </span>
                      </div>
                    </div>
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="remarks_input_holder">
          <Avatar
            size={32}
            icon={<UserOutlined />}
            src={userDetails?.profilePic}
          />
          <div className="remarks_input_wrap">
            <div style={{ flexGrow: 1 }}>
              <TextArea
                key={loader ? 'loading' : 'not-loading'}
                autoFocus
                autoSize
                bordered={false}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                disabled={loader}
                onPressEnter={(e) => {
                  if (remark.trim().length === 0) {
                    return e.preventDefault();
                  }

                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    return handleCreateRemark();
                  }
                }}
                placeholder="Write a remark..."
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

RemarksTab.propTypes = {};

export default RemarksTab;
